<template>
  <div v-bind:key="refreshKey">
    <slot name="table-top-actions" v-bind:selectedIds="selectedIds"></slot>
    <b-table responsive v-handy-scroll v-bind:items="rows" v-bind:fields="fields" no-local-sorting
             v-bind:sort-by.sync="sort.field" v-bind:sort-desc.sync="sortIsDesc" v-resizable
             striped hover @row-clicked="onRowClicked" v-bind:busy="loading"
             @sort-changed="onSortChanged"
             style="white-space: nowrap; padding-bottom: 30px"
    >
      <template #head(selection)="data">
        <b-form-checkbox v-bind:indeterminate="isIndeterminate(selected)" :checked="selected.all"
                         style="margin-top: 7px"
                         @change="val=>toggleAllRow(val)" inline
        ></b-form-checkbox>
      </template>
      <template #cell(selection)="data">
        <b-form-checkbox v-model="selectedIds[data.item.id]" inline @input="val=>toggleRow(data.item, val)"
        ></b-form-checkbox>
      </template>
      <!-- Organizations: compact chips (top 3 + "+N"), full list in the tooltip -->
      <template #cell(orgIds)="data">
        <span v-if="(data.value || []).length" v-b-tooltip.hover.left :title="orgsTooltip(data.value)">
          <span v-for="org in (data.value || []).slice(0, 3)" :key="org" class="ut-chip">
            <span class="ut-chip__dot" :style="{ backgroundColor: getOrganizationColorById(org) || '#7367f0' }"></span>
            {{ getOrganizationById(org).name }}
          </span>
          <b-badge v-if="data.value.length > 3" variant="light-secondary">+{{ data.value.length - 3 }}</b-badge>
        </span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(roleId)="data">
        <span class="ut-chip ut-chip--role">{{ roleById(data.value).name }}</span>
      </template>
      <!-- Business units: same chip pattern -->
      <template #cell(businessUnits)="data">
        <span v-if="(data.value || []).length" v-b-tooltip.hover.left :title="busTooltip(data.value)">
          <span v-for="buid in (data.value || []).slice(0, 3)" :key="buid" class="ut-chip">
            {{ getBusinessUnitById(buid).name }}
          </span>
          <b-badge v-if="data.value.length > 3" variant="light-secondary">+{{ data.value.length - 3 }}</b-badge>
        </span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(creationtime)="data">
        {{
          $moment(data.value)
              .format('DD.MM.YYYY HH:mm')
        }}
      </template>
      <template #cell(avatar)="data">

        <b-avatar
            size="30"
            variant="light-primary"
            :src="require(`@/assets/images/avatars/${data.value || '1'}.png`)"
        />
      </template>
      <template #cell(active)="data">
        <div>
          <span v-if="data.value" class="icon is-success">
      <i class="fas fa-check" style="color:green;"></i>
    </span>

          <span v-else class="icon is-danger">
      <i class="fas fa-times" style="color:red;"></i>
    </span>
        </div>
      </template>

         <template #cell(notRestrictedIP)="data">
        <div>
          <span v-if="data.value" class="icon is-success">
      <i class="fas fa-check" style="color:green;"></i>
    </span>

          <span v-else class="icon is-danger">
      <i class="fas fa-times" style="color:red;"></i>
    </span>
        </div>
      </template>

      <!-- USER_ACTIONS: soft-hide/unhide (nothing is deleted) -->
      <template #cell(actions)="data">
        <div class="text-center">
          <b-badge v-if="data.item.hidden" variant="light-secondary" class="mr-50">{{ $t('users.hidden') }}</b-badge>
          <b-button size="sm" :variant="data.item.hidden ? 'outline-success' : 'outline-secondary'"
                    v-b-tooltip v-bind:title="data.item.hidden ? $t('users.unhide') : $t('users.hide')"
                    @click.stop="onHide(data.item)">
            <i :class="data.item.hidden ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'"></i>
          </b-button>
        </div>
      </template>

    </b-table>
    <!--{{pagination}}-->
    <div class="mt-2 text-center">
      <div class="d-inline-block">
        <b-pagination
            v-model="pagination.page"
            v-bind:total-rows="pagination.total"
            v-bind:per-page="pagination.size"
            @page-click="onPageClick"
            align="center"
        ></b-pagination>
      </div>
      <div class="d-inline-block">
        <b-select class="d-inline-block" style="width: 70px" size="sm" v-model="pagination.size" @change="refresh">

          <b-select-option v-bind:value="25">25</b-select-option>
          <b-select-option v-bind:value="50">50</b-select-option>
          <b-select-option v-bind:value="100">100</b-select-option>
        </b-select>
        /{{ pagination.total }} {{ $t('columns.counterNames.users') }}

      </div>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import mixinPermissions from '@/components/mixin/mixinPermissions';

export default {
  name: 'UsersTable',
  mixins: [mixinBase, mixinTable, mixinNotifications, mixinPermissions],
  data: () => ({
    rows: [],
    selected: { all: false },
  }),
  props: {
    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    hasSelection: {
      default: () => false
    },
    lazy: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },
  created() {
    this.setPagination(1, 0, 25);
    if (!this.lazy) {
      this.refresh();
    }
  },
  computed: {
    ...mapGetters('data', ['getOrganizationById', 'getOrganizationColorById', 'getBusinessUnitById']),
    ...mapGetters('users', ['roleById']),

    fields() {
      let $this = this;
      let fields = (this.columns || []).map(column => {
        return {
          key: column.key,
          label: $this.$t(`columns.${column.key}`),
          sortable: true,
          active: column.enable,
        };
      });
      if (this.hasPermission(this.permissions.USER_ACTIONS)) {
        fields.push({
          key: 'actions',
          label: $this.$t('columns.actions'),
          class: 'text-center',
          sortable: false,
          active: true,
        });
      }
      if (this.hasSelection) {
        fields.unshift({
          key: 'selection',
          label: '',
          sortable: false,
          active: true,
        });
      }
      return fields.filter(f => f.active === true);
    },
    selectedIds() {
      this.refreshKey; // Little hack to recompile
      // console.log("selectedIds()",this.selected)
      return Object.keys(this.selected)
          .filter(k => k !== 'all')
          .reduce((ans, id) => Object.assign(ans, { [id]: true }), {});
    },
  },
  methods: {
    // Soft-hide/unhide a single user (nothing is deleted)
    onHide(user) {
      const $this = this;
      const hide = !user.hidden;
      const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
      this.$bvModal.msgBoxConfirm(this.$t(hide ? 'users.hideConfirm' : 'users.unhideConfirm', { name }), {
        title: this.$t('globalMessages.pleaseConfirm'),
        size: 'sm',
        buttonSize: 'sm',
        okVariant: hide ? 'warning' : 'success',
        okTitle: this.$t('operations.yes'),
        cancelTitle: this.$t('operations.no'),
        centered: true,
      }).then(ok => {
        if (!ok) return;
        $this.hideUser({ id: user.id, hidden: hide })
            .then(() => {
              $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: name }));
              $this.refresh();
            })
            .catch(err => {
              $this.showErrorNotification($this.$t('globalMessages.updateDataError', { system: name }), $this.getErrorDescription(err));
            });
      });
    },
    // Full lists for the chips' hover tooltips
    orgsTooltip(ids) {
      return (ids || []).map(id => this.getOrganizationById(id).name).filter(Boolean).join(', ');
    },
    busTooltip(ids) {
      return (ids || []).map(id => this.getBusinessUnitById(id).name).filter(Boolean).join(', ');
    },
    ...mapActions('users', ['getAllUsers', 'hideUser']),
    async refresh() {
      let $this = this;
      let loadingToken = this.setLoading();
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'lastUpdate';
      }
      // console.log("refresh()", {  pagination: this.pagination, sort})
      const result = await this.getAllUsers(
          {
            pagination: this.pagination,
            sort,
            filter: this.filters,
          }
      )
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.users') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      this.rows = result.data;
      this.setMetaDataFromResult(result);
      this.setLoaded(loadingToken);
    }
  }
};
</script>

<style scoped lang="scss">
/* Same chip language as the affiliate dashboard's countries/statuses cells */
.ut-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(110, 117, 159, 0.1);
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  margin-right: 0.3rem;
  font-size: 0.8rem;

  &__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &--role {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
    font-weight: 600;
  }
}
</style>

<style lang="scss">
.dark-layout .ut-chip {
  background: rgba(255, 255, 255, 0.08);

  &--role {
    background: rgba(115, 103, 240, 0.2);
    color: #9e95f5;
  }
}
</style>
